// DS3.5
import React, { useMemo } from 'react';
import c from 'classnames';
import InputErrorMessage from '../../../formControls/lego/InputErrorMessage';
import InputLabelText from '../../../formControls/lego/InputLabelText';
import InputLabelSubText from '../../../formControls/lego/InputLabelSubText';
import FormElement from '../../../formControls/layouts/FormElement';
import noop from 'lodash/noop';
import type { Props as SelectFieldProps } from './SelectField';
import NativeSelectFieldContent from './NativeSelectFieldContent';
import s from './SelectField.scss';

export interface Props extends SelectFieldProps {}

const NativeSelectField: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  className,
  label,
  subLabel,
  isLabelHidden,
  isRequired,
  details,
  error,
  items,
  value,
  prefix: prefixProp,
  onSelect = noop,
  noBottomSpacing = true,
  showPrefixIcons = false,
  openBiData,
  closeBiData,
  allowComplexLabelOnSelected: _allowComplexLabelOnSelected,
  isOpen: _isOpen,
  ...rest
}) => {
  const prefix = useMemo(() => {
    if (prefixProp || !showPrefixIcons) {
      return prefixProp;
    }
    const selectedItem = items.find(item => `${item.value}` === `${value}`);
    return selectedItem?.icon;
  }, [prefixProp, showPrefixIcons, items, value]);

  return (
    <div className={c(s['root'], className)}>
      <FormElement
        inputId={id}
        label={
          <InputLabelText isRequired={isRequired} className="label">
            {label}
          </InputLabelText>
        }
        subLabel={
          subLabel && (
            <InputLabelSubText className="sublabel">
              {subLabel}
            </InputLabelSubText>
          )
        }
        isLabelHidden={isLabelHidden}
        noBottomSpacing={noBottomSpacing}
        details={
          details ||
          (error && (
            <InputErrorMessage id={`${id}-error`}>{error}</InputErrorMessage>
          ))
        }
      >
        <NativeSelectFieldContent
          id={id}
          onSelect={onSelect}
          required={isRequired}
          hasError={!!error}
          items={items}
          value={value}
          prefix={prefix}
          openBiData={openBiData}
          closeBiData={closeBiData}
          {...rest}
        />
      </FormElement>
    </div>
  );
};
NativeSelectField.displayName = 'NativeSelectField';

export default NativeSelectField;
